import { DataPoint, MetricType } from '../../pages/Dashboard';
import { TrendingDown, TrendingUp, Leaf, Trash2, Trees, GraduationCap, BookOpen, Video, FileText, ArrowRight } from 'lucide-react';

interface InfoPanelProps {
  netImpact: number;
  selectedMetric: MetricType;
  data: DataPoint[];
  onOpenCEUniversity: () => void;
}

export function InfoPanel({ netImpact, selectedMetric, data, onOpenCEUniversity }: InfoPanelProps) {
  const getMetricUnit = () => {
    switch (selectedMetric) {
      case 'CO2e':
        return 'tCO₂e';
      case 'Waste':
        return 'kg';
      case 'TreesSaved':
        return 'trees';
      default:
        return 'tCO₂e';
    }
  };

  const getMetricIcon = () => {
    switch (selectedMetric) {
      case 'CO2e':
        return <Leaf className="w-8 h-8" />;
      case 'Waste':
        return <Trash2 className="w-8 h-8" />;
      case 'TreesSaved':
        return <Trees className="w-8 h-8" />;
      default:
        return <Leaf className="w-8 h-8" />;
    }
  };

  const getMetricTitle = () => {
    switch (selectedMetric) {
      case 'CO2e':
        return 'Carbon Emissions';
      case 'Waste':
        return 'Waste Reduction';
      case 'TreesSaved':
        return 'Trees Saved Equivalent';
      default:
        return 'Carbon Emissions';
    }
  };

  const getMetricValue = (item: DataPoint) => {
    switch (selectedMetric) {
      case 'CO2e':
        return item.co2e;
      case 'Waste':
        return item.waste;
      case 'TreesSaved':
        return item.treesSaved;
      default:
        return item.co2e;
    }
  };

  const positiveItems = data.filter(item => getMetricValue(item) < 0);
  const negativeItems = data.filter(item => getMetricValue(item) > 0);
  const totalReductions = positiveItems.reduce((sum, item) => sum + Math.abs(getMetricValue(item)), 0);
  const totalIncreases = negativeItems.reduce((sum, item) => sum + getMetricValue(item), 0);

  // For Trees Saved: positive values = positive impact
  // For CO2e and Waste: negative values = positive impact
  const isPositiveNet = selectedMetric === 'TreesSaved' ? netImpact > 0 : netImpact < 0;

  // For breakdown: determine if items contribute positively based on metric type
  const getIsPositiveImpact = (value: number) => {
    return selectedMetric === 'TreesSaved' ? value > 0 : value < 0;
  };

  return (
    <div className="space-y-6">
      {/* Main Impact Card */}
      <div className={`rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.12)] hover:shadow-[0_20px_60px_rgb(0,0,0,0.18)] transition-all duration-500 p-6 relative overflow-hidden group hover:scale-[1.02] ${isPositiveNet ? 'bg-gradient-to-br from-green-50 via-green-100/80 to-emerald-100 border-2 border-[#8CC63F]/30' : 'bg-gradient-to-br from-red-50 via-red-100/80 to-rose-100 border-2 border-[#D71920]/30'}`}>
        {/* Shine effect on hover */}
        <div className={`absolute inset-0 bg-gradient-to-tr ${isPositiveNet ? 'from-green-200/0 via-green-200/20 to-green-200/0' : 'from-red-200/0 via-red-200/20 to-red-200/0'} opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none`} />

        <div className="relative z-10">
          <div className="flex items-start justify-between mb-4">
            <div className={isPositiveNet ? 'text-[#8CC63F]' : 'text-[#D71920]'}>
              {getMetricIcon()}
            </div>
            {isPositiveNet ? (
              <TrendingDown className="w-6 h-6 text-[#8CC63F]" />
            ) : (
              <TrendingUp className="w-6 h-6 text-[#D71920]" />
            )}
          </div>

          <h3 className="text-gray-700 mb-2">Net Impact (12 Months)</h3>
          <p className={`text-4xl mb-2 ${isPositiveNet ? 'text-[#8CC63F]' : 'text-[#D71920]'}`}>
            {netImpact > 0 ? '+' : ''}{Math.abs(netImpact).toLocaleString()}
          </p>
          <p className="text-sm text-gray-600">{getMetricUnit()} {isPositiveNet ? 'avoided' : 'added'}</p>

          <div className={`mt-4 p-3 rounded-lg backdrop-blur-sm ${isPositiveNet ? 'bg-white/60' : 'bg-white/60'}`}>
            <p className="text-sm">
              {isPositiveNet ? (
                <span className="text-green-800">
                  ✓ Overall positive environmental impact achieved through operational improvements
                </span>
              ) : (
                <span className="text-red-800">
                  ✗ Net increase in environmental impact - review areas for improvement
                </span>
              )}
            </p>
          </div>
        </div>
      </div>

      {/* Context Card */}
      <div className="bg-gradient-to-br from-blue-50 to-indigo-50/80 backdrop-blur-sm border border-blue-200/50 rounded-2xl shadow-[0_4px_20px_rgb(0,114,206,0.08)] hover:shadow-[0_8px_30px_rgb(0,114,206,0.12)] transition-all duration-300 p-4 hover:scale-[1.02]">
        <h4 className="text-sm text-gray-900 mb-2">Understanding {getMetricTitle()}</h4>
        <p className="text-xs text-gray-600">
          {selectedMetric === 'CO2e' && 'Carbon dioxide equivalent (CO₂e) measures total greenhouse gas emissions. Negative values indicate reductions.'}
          {selectedMetric === 'Waste' && 'Waste measurement in kilograms. Negative values represent waste diverted from landfills.'}
          {selectedMetric === 'TreesSaved' && 'Trees saved equivalent represents the number of mature trees needed to offset the carbon impact.'}
        </p>
      </div>

      {/* Breakdown Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-gray-900 mb-6">Impact Breakdown</h3>

        <div className="space-y-0">
          {/* Total Reductions */}
          <div className="py-4 border-b border-gray-200">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm text-gray-600">Total Reductions</span>
              <span className="text-sm text-[#8CC63F]">{positiveItems.length} initiatives</span>
            </div>
            <p className="text-[#8CC63F]">
              -{totalReductions.toLocaleString()} {getMetricUnit()}
            </p>
          </div>

          {/* Total Increases */}
          <div className="py-4 border-b border-gray-200">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm text-gray-600">Total Increases</span>
              <span className={`text-sm ${selectedMetric === 'TreesSaved' ? 'text-[#8CC63F]' : 'text-[#D71920]'}`}>{negativeItems.length} items</span>
            </div>
            <p className={selectedMetric === 'TreesSaved' ? 'text-[#8CC63F]' : 'text-[#D71920]'}>
              +{totalIncreases.toLocaleString()} {getMetricUnit()}
            </p>
          </div>

          {/* Net Result */}
          <div className="pt-4">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm text-gray-600">Net Result</span>
              <span className={`text-sm ${isPositiveNet ? 'text-[#8CC63F]' : 'text-[#D71920]'}`}>
                {isPositiveNet ? 'Positive' : 'Negative'}
              </span>
            </div>
            <p className={isPositiveNet ? 'text-[#8CC63F]' : 'text-[#D71920]'}>
              {netImpact > 0 ? '+' : ''}{netImpact.toLocaleString()} {getMetricUnit()}
            </p>
          </div>
        </div>
      </div>

      {/* CE University Card */}
      <div
        onClick={onOpenCEUniversity}
        className="bg-gradient-to-br from-indigo-600 via-blue-600 to-cyan-600 rounded-2xl shadow-[0_8px_30px_rgba(59,130,246,0.35)] hover:shadow-[0_12px_40px_rgba(59,130,246,0.5)] transition-all duration-500 p-6 cursor-pointer group hover:scale-[1.02] relative overflow-hidden"
      >
        {/* Animated background effect */}
        <div className="absolute inset-0 bg-gradient-to-tr from-white/0 via-white/10 to-white/0 opacity-0 group-hover:opacity-100 transition-opacity duration-700" />

        <div className="relative z-10">
          <div className="flex items-center justify-between mb-4">
            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-3">
              <GraduationCap className="w-8 h-8 text-white" />
            </div>
            <ArrowRight className="w-6 h-6 text-white/80 group-hover:translate-x-1 transition-transform duration-300" />
          </div>

          <h3 className="text-white text-xl mb-2">CE University</h3>
          <p className="text-white/90 text-sm mb-4">
            Unlock free educational resources on Circular Economy, EPR, and sustainable practices
          </p>

          {/* Resource Stats */}
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-2 text-center">
              <Video className="w-4 h-4 text-white/90 mx-auto mb-1" />
              <p className="text-white text-xs">Videos</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-2 text-center">
              <FileText className="w-4 h-4 text-white/90 mx-auto mb-1" />
              <p className="text-white text-xs">Guides</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-2 text-center">
              <BookOpen className="w-4 h-4 text-white/90 mx-auto mb-1" />
              <p className="text-white text-xs">Articles</p>
            </div>
          </div>

          {/* Call to Action */}
          <div className="bg-white/20 backdrop-blur-sm rounded-lg p-3 border border-white/30">
            <p className="text-white text-sm flex items-center gap-2">
              <span className="bg-green-400 text-green-900 text-xs px-2 py-0.5 rounded">NEW</span>
              6+ resources available now
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
